import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, MenuItem, Pagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import styles from "./BlogSearchResults.module.css";
import { searchBlogArticles } from "../../utils/meilisearchClient";
import BlogSearchResultSkeleton from "../../components/Skeletons/BlogSearchResultSkeleton/BlogSearchResultSkeleton";
import { SearchResponse } from "meilisearch";
import { BlogArticle } from "../../types-var/blog";
import BlogSearchResultsSEO from "../../components/SEO/BlogSearchResultsSEO";

type SortOption =
  | "popularity:desc"
  | "title:asc"
  | "title:desc"
  | "publishedAt:desc";

const sortOptions = [
  { value: "popularity:desc", label: "Más populares" },
  { value: "title:asc", label: "Título A-Z" },
  { value: "title:desc", label: "Título Z-A" },
  { value: "publishedAt:desc", label: "Más recientes" },
];

const BlogSearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const currentSort = (searchParams.get("sort") ||
    "popularity:desc") as SortOption;
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const HITS_PER_PAGE = 12;

  const [searchQuery, setSearchQuery] = useState(query);
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);

  const fetchResults = async () => {
    try {
      const results: SearchResponse<BlogArticle> = await searchBlogArticles(
        query,
        {
          sort: [currentSort],
          page: currentPage,
          hitsPerPage: HITS_PER_PAGE,
        }
      );

      setArticles(results.hits);
      setTotalPages(
        Math.ceil((results.estimatedTotalHits ?? 0) / HITS_PER_PAGE)
      );
    } catch (error) {
      console.error("Search failed:", error);
      setError("Failed to load search results");
    } finally {
      setLoading(false);
    }
  };

  const memoizedFetchResults = React.useCallback(fetchResults, [
    query,
    currentSort,
    currentPage,
    HITS_PER_PAGE,
  ]);

  useEffect(() => {
    setLoading(true);
    memoizedFetchResults();
  }, [memoizedFetchResults]);

  const handleSortChange = (event: any) => {
    const newSort = event.target.value as SortOption;
    setSearchParams({ q: query, sort: newSort, page: "1" });
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setSearchParams({ q: query, sort: currentSort, page: page.toString() });
    window.scrollTo(0, 0);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({
        q: searchQuery.trim(),
        sort: currentSort,
        page: "1",
      });
    }
  };

  if (loading) {
    return <BlogSearchResultSkeleton />;
  }

  if (error) {
    return <div className={styles.errorState}>{error}</div>;
  }

  if (!loading && articles.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No se encontraron resultados para "{query}"</p>
        <p>Intenta con otras palabras clave</p>
      </div>
    );
  }

  return (
    <div
      className={styles.searchContainer}
      aria-label="Resultados de búsqueda del blog de Evefant"
    >
      <BlogSearchResultsSEO query={query} />
      {/* Improved header section */}
      <div className={styles.searchHero} aria-label="Encabezado de búsqueda">
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            <span className={styles.heroHighlight}>Resultados</span> para
            <span className={styles.heroQuery}>&nbsp;"{query}"</span>
          </h1>
          <p className={styles.resultsCount} aria-live="polite">
            {articles.length} artículo{articles.length === 1 ? "" : "s"}{" "}
            encontrado{articles.length === 1 ? "" : "s"}
          </p>
          <form
            onSubmit={handleSearch}
            className={styles.heroSearchForm}
            role="search"
            aria-label="Buscar artículos en el blog"
          >
            <div className={styles.heroSearchInputWrapper}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar más artículos..."
                className={styles.heroSearchInput}
                aria-label="Campo de búsqueda de artículos"
              />
              <button
                type="submit"
                className={styles.heroSearchButton}
                aria-label="Buscar"
              >
                <SearchIcon className={styles.heroSearchIcon} />
              </button>
            </div>
          </form>
          <div className={styles.heroSortControl}>
            <span className={styles.heroSortLabel}>Ordenar por:</span>
            <Select
              value={currentSort}
              onChange={handleSortChange}
              className={styles.heroSortSelect}
              size="small"
              inputProps={{ "aria-label": "Ordenar resultados" }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div
        className={styles.results}
        aria-label="Lista de artículos encontrados"
        role="list"
      >
        {articles.map((article) => (
          <BlogArticleCard key={article.id} article={article} />
        ))}
      </div>

      {totalPages > 1 && (
        <div
          className={styles.pagination}
          aria-label="Paginación de resultados"
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            aria-label="Navegación de páginas"
          />
        </div>
      )}
    </div>
  );
};

// Extract BlogArticleCard as a separate component
const BlogArticleCard = ({ article }: { article: BlogArticle }) => {
  const imageBaseUrl = process.env.REACT_APP_IMAGE_FOLDER;

  return (
    <article
      className={styles.searchResult}
      aria-label={`Artículo: ${article.title}`}
      role="listitem"
    >
      {article.cover && (
        <div className={styles.resultImage}>
          <img
            src={
              article.cover.formats?.thumbnail?.url
                ? `${article.cover.formats.thumbnail.url}`
                : article.cover.url
                ? `${article.cover.url}`
                : ""
            }
            alt={article.title}
          />
        </div>
      )}
      <div className={styles.resultContent}>
        <div className={styles.resultCategories}>
          {article.blog_categories?.map((category: any) => (
            <span key={category.id} className={styles.category}>
              {category.name}
            </span>
          ))}
        </div>
        <h2 className={styles.resultTitle}>
          <a href={`/blog/${article.url}`}>{article.title}</a>
        </h2>
        <p className={styles.resultDescription}>{article.description}</p>
        {article.blog_author && (
          <div className={styles.resultAuthor}>
            {article.blog_author.avatar &&
              (article.blog_author.avatar.formats?.thumbnail?.url ||
                article.blog_author.avatar.url) && (
                <img
                  src={
                    article.blog_author.avatar.formats?.thumbnail?.url
                      ? `${article.blog_author.avatar.formats.thumbnail.url}`
                      : article.blog_author.avatar.url
                  }
                  alt={article.blog_author.name}
                  className={styles.authorAvatar}
                />
              )}
            <span>Por {article.blog_author.name}</span>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogSearchResults;
